import './App.css';
import React, { lazy, Suspense, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigationType } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './Components/ProtectedRoute';
import Home from './Pages/Home';
import Navbar from './Components/Navbar';
import Footer from './Components/Footer';
import DynamicGallery from './Pages/DynamicGallery';

// Pages most visitors never open are downloaded only when needed.
const Contact = lazy(() => import('./Pages/Contact'));
const Login = lazy(() => import('./Pages/Login'));
const Admin = lazy(() => import('./Pages/Admin'));
const Mountains = lazy(() => import('./Pages/Mountains'));
const Cities = lazy(() => import('./Pages/Cities'));
const Automotive = lazy(() => import('./Pages/Automotive'));
const Beach = lazy(() => import('./Pages/Beach'));

const scrollPositions = new Map();

// New pages open at the top; going back returns to where you were on that page.
function ScrollManager() {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    window.history.scrollRestoration = 'manual';
  }, []);

  useLayoutEffect(() => {
    const { key } = location;
    window.scrollTo(0, navigationType === 'POP' ? scrollPositions.get(key) || 0 : 0);
    let lastY = window.scrollY;
    const onScroll = () => {
      lastY = window.scrollY;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      scrollPositions.set(key, lastY);
    };
    // Only page changes count: opening a photo (?photo=) must not scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return null;
}

function Page() {
  const { pathname } = useLocation();
  return (
    <main key={pathname} className="page-enter min-h-[calc(100svh-3.5rem)]">
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/gallery/:categorySlug" element={<DynamicGallery />} />
          <Route path="/gallery/:categorySlug/:albumSlug" element={<DynamicGallery />} />
          <Route path="/mountains" element={<Mountains />} />
          <Route path="/cities" element={<Cities />} />
          <Route path="/automotive" element={<Automotive />} />
          <Route path="/seaside" element={<Beach />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
    </main>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <div data-theme="lofi" className="App">
          <ScrollManager />
          <Navbar />
          <Page />
          <Footer />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
